/**
 * Local types for the dataset-export sub-feature.
 *
 * Mirror the BE's `ExportDatasetsHttpRequest` shape and the four filter
 * axes the modal exposes. Visibility / Archived live as discriminated
 * unions so the View renders them deterministically.
 */

import {
  PROJECT_DATA_TYPES,
  PROJECT_USAGE_VALUES,
  type ProjectDataType,
  type ProjectUsage,
} from 'features/projects/constants'
import type { ProjectFilters } from 'features/projects/utils/project-filters'

export type VisibilityFilter = 'any' | 'internal' | 'external'
export type ArchivedFilter = 'any' | 'active' | 'archived'
export type GroupingFilter = 'dataset' | 'hub' | 'account' | 'account_group'

export type ExportFilters = {
  visibility: VisibilityFilter
  archived: ArchivedFilter
  data_types: ProjectDataType[]
  usage: ProjectUsage[]
  grouping: GroupingFilter
  project_uuids?: string[]
}

export type ExportStatus = 'idle' | 'preparing' | 'success' | 'error'

/**
 * Domain-shaped result of an export call: the CSV payload plus the
 * filename the BE suggested via Content-Disposition (null when the
 * header is absent). Keeps transport types (AxiosResponse) inside the
 * service per japang's `no-transport-type-leak`.
 */
export type ExportDownload = {
  blob: Blob
  filename: string | null
}

export const DEFAULT_EXPORT_FILTERS: ExportFilters = {
  visibility: 'any',
  archived: 'active',
  data_types: [],
  usage: [],
  grouping: 'dataset',
}

/**
 * Seeds the export modal from the homepage's applied dataset filters so
 * the two surfaces agree when the modal opens. Only the four axes the
 * export supports carry over (visibility/archived/data types/usage);
 * count, state, and date filters have no export equivalent. The
 * `__none__` sentinel and any unknown values are dropped by the
 * membership narrowing — the export wire format has no "none" bucket.
 */
export const seedExportFilters = (
  pageFilters: ProjectFilters,
): ExportFilters => ({
  ...DEFAULT_EXPORT_FILTERS,
  visibility: pageFilters.internal,
  archived: pageFilters.archived,
  data_types: pageFilters.data_types.filter((v): v is ProjectDataType =>
    (PROJECT_DATA_TYPES as readonly string[]).includes(v),
  ),
  usage: pageFilters.usage.filter((v): v is ProjectUsage =>
    (PROJECT_USAGE_VALUES as readonly string[]).includes(v),
  ),
})
